import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Providers } from './app/providers';
import { router } from './app/router';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles/globals.css';

// If the user hits the site mid-deploy (or has a stale chunk cached), Vite's
// lazy-loaded routes can throw:
//   "Failed to fetch dynamically imported module: .../assets/<chunk>.js"
// This can leave a feature tab (e.g. DAM) "broken" until refresh.
// We auto-reload to self-heal: once per deploy, at most once per 30s so a chunk
// that is missing from the new build too cannot loop (the ErrorBoundary then asks).
const CHUNK_RELOAD_KEY = 'dc:chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 30_000;

function reloadForNewDeploy() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  } catch {
    // ignore
  }
}

function isDynamicImportFailure(err: unknown): boolean {
  const msg =
    typeof err === 'string'
      ? err
      : (err as any)?.message || (err as any)?.toString?.() || '';
  return (
    typeof msg === 'string' &&
    (msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError'))
  );
}

// Vite fires this for every failed lazy import, including the ones React Router
// catches during render (those never reach unhandledrejection).
window.addEventListener('vite:preloadError', () => reloadForNewDeploy());

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (isDynamicImportFailure(event.reason)) reloadForNewDeploy();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </ErrorBoundary>
  </React.StrictMode>,
);
// Cache bust 08/21/2025 11:35:32
